/**
 * scoreMatch — THE strong / possible rule. Signals are computed in
 * `signals.ts`; this file only combines them.
 *
 *  strong   — no number conflict, the record is not already documented, and
 *             either (a) the invoice number matches (normalised length ≥ 4)
 *             and the vendor does not contradict it, or (b) vendor + exact
 *             amount + date within the window.
 *  possible — not strong, and either vendor + date within the window, or a
 *             number match the vendor does not contradict (a short number,
 *             or an already-documented record).
 *  null     — not a candidate.
 */
import { diffDaysYmd } from "../format/date.js";
import { MAX_INVOICE_NUMBER_LENGTH, matchReasonEnum, } from "../schemas/inboxMatches.js";
import { isoDayOrNull, normalizeCurrency, normalizeDocumentNumber, toCents, } from "./normalize.js";
import { isOccurrenceNumber, isRecurringPlaceholder } from "./placeholder.js";
import { amountSignal, dateSignal, documentDay, numberSignal, vendorSignal, } from "./signals.js";
/**
 * The record already holds its document: an `invoice`/`receipt` attachment,
 * or a recurring bill whose placeholder number has been typed over. Read
 * from the record itself, never from (RLS-filtered) inbox messages.
 */
export function isAlreadyDocumented(candidate) {
    if (candidate.hasDocumentAttachment)
        return true;
    return (candidate.kind === "ap_bill" &&
        !!candidate.occurrenceNumber &&
        !isOccurrenceNumber(candidate.documentNumber, candidate.occurrenceNumber));
}
/** Signed `a − b` in calendar days; null when either side is missing or invalid. */
function dayDiff(a, b) {
    return a && b ? diffDaysYmd(b, a) : null;
}
export function buildDiffs(doc, candidate, ctx) {
    const org = normalizeCurrency(ctx.orgCurrency);
    const docCurrency = normalizeCurrency(doc.currency);
    const recordCurrency = normalizeCurrency(candidate.currency);
    const sameCurrency = (docCurrency ?? org) === (recordCurrency ?? org);
    const documentDate = documentDay(doc);
    const documentDueDate = isoDayOrNull(doc.dueDate);
    return {
        documentVendor: doc.vendorName?.trim() || null,
        documentNumber: doc.invoiceNumber?.trim() || null,
        recordNumber: candidate.documentNumber,
        documentAmount: doc.total,
        documentCurrency: docCurrency,
        recordAmount: candidate.amount,
        recordCurrency,
        amount: doc.total != null && sameCurrency
            ? (toCents(doc.total) - toCents(candidate.amount)) / 100
            : null,
        documentDate,
        dateDays: dayDiff(documentDate, candidate.date),
        documentDueDate,
        dueDateDays: dayDiff(documentDueDate, candidate.dueDate),
    };
}
/**
 * The bill placeholder replacement (or the "not replaced" note). A parsed
 * number longer than the PATCH accepts ({@link MAX_INVOICE_NUMBER_LENGTH})
 * is never offered.
 */
export function buildFills(doc, candidate) {
    const to = doc.invoiceNumber?.trim();
    const current = candidate.documentNumber;
    if (candidate.kind !== "ap_bill" ||
        !to ||
        to.length > MAX_INVOICE_NUMBER_LENGTH ||
        !current ||
        !isOccurrenceNumber(current, candidate.occurrenceNumber) ||
        normalizeDocumentNumber(to) === normalizeDocumentNumber(current)) {
        return {};
    }
    return isRecurringPlaceholder({
        currentNumber: current,
        occurrenceNumber: candidate.occurrenceNumber,
        approvalStatus: candidate.approvalStatus,
    })
        ? { invoiceNumber: { from: current, to } }
        : { invoiceNumberBlocked: { current, reason: "not_pending" } };
}
const REASON_ORDER = new Map(matchReasonEnum.options.map((r, i) => [r, i]));
export function scoreMatch(doc, candidate, ctx) {
    const number = numberSignal(doc, candidate);
    const vendor = vendorSignal(doc, candidate);
    const amount = amountSignal(doc, candidate, ctx);
    const date = dateSignal(doc, candidate);
    const alreadyDocumented = isAlreadyDocumented(candidate);
    const numberCase = number.hit && !vendor.conflict;
    const strong = !number.conflict &&
        !alreadyDocumented &&
        ((numberCase && number.strongEnough) || (vendor.hit && amount.exact && date.hit));
    const possible = (vendor.hit && date.hit) || numberCase;
    const reasons = [number.reason, vendor.reason, amount.reason, date.reason]
        .filter((r) => r !== null);
    if (alreadyDocumented)
        reasons.push("already_documented");
    reasons.sort((a, b) => REASON_ORDER.get(a) - REASON_ORDER.get(b));
    return {
        strength: strong ? "strong" : possible ? "possible" : null,
        reasons,
        alreadyDocumented,
        diffs: buildDiffs(doc, candidate, ctx),
        fills: buildFills(doc, candidate),
    };
}
//# sourceMappingURL=score.js.map